import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, UseFormSetValue } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowLeft,
  Save,
  Trash2,
  Plus,
  AlertCircle,
  CheckCircle,
  Upload,
  Download,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'react-toastify';
import { chatbotAdminApi, getChatbotApiErrorMessage } from '../../api/chatbotApi';
import { platformAdminApi } from '@/features/platform-admin/api/platformAdminApi';
import {
  BotConfigurationForm,
  type BotConfigurationFormData,
} from '../../components/admin/BotConfigurationForm';
import { TenantAiKeysCard } from '../../components/admin/TenantAiKeysCard';
import { noragFormToPayload } from '../../api/noragFormPayload';
import { conciergeFormToPayload } from '../../api/conciergeFormPayload';
import { infoFieldsFormToPayload } from '../../api/infoFieldsFormPayload';
import { padChatbotInfoFields } from '@workspace/types';
import { BotInfoFieldsQuickDialog } from '../../components/admin/BotInfoFieldsQuickDialog';
import {
  hasTenantProviderKeyForCreate,
  providerKeyCreateErrorMessage,
} from '../../utils/providerKeyValidation';
import type {
  TenantAiKeysResponse,
  ChatbotAiCatalog,
  ChatbotBotInstance,
  ChatbotWhitelistEntry,
  ChatbotAccessType,
  ChatbotVerificationMethod,
} from '../../types/chatbot.types';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentUser } from '@/features/auth/authSlice';
import { hasModuleFeature, hasWhatsAppModuleAccess } from '@/features/auth/moduleFeatures';
import type { PlatformTenant } from '@/features/platform-admin/types/platformAdmin.types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { InstanceOutboundChannel, TenantFeature, WhatsAppChannel, TelegramChannel, FacebookChannel, InstagramChannel } from '@workspace/types';
import { InstanceOutboundChannelsCard } from './InstanceOutboundChannelsCard';
import { InstanceCampaignChannelsCard } from './InstanceCampaignChannelsCard';
import { whatsappAdminApi } from '@/features/whatsapp/api/whatsappApi';
import { WhatsAppPhoneWhitelistSection } from '@/features/whatsapp/components/WhatsAppPhoneWhitelistSection';
import { telegramAdminApi } from '@/features/telegram/api/telegramApi';
import { TelegramUserWhitelistSection } from '@/features/telegram/components/TelegramUserWhitelistSection';
import { facebookAdminApi } from '@/features/facebook/api/facebookApi';
import { instagramAdminApi } from '@/features/instagram/api/instagramApi';
import { FacebookUserWhitelistSection } from '@/features/facebook/components/FacebookUserWhitelistSection';
import { InstagramUserWhitelistSection } from '@/features/instagram/components/InstagramUserWhitelistSection';
import { InstanceOtpEmailSection } from '@/features/chatbot/components/admin/InstanceOtpEmailSection';
const DEFAULT_ALLOWED_FILE_EXTENSIONS = '.pdf,.doc,.docx,.txt,.xls,.xlsx';

// Base schema sin tenantId
const baseInstanceSchema = z.object({
  name: z.string().min(1, 'Nombre requerido'),
  description: z.string().optional(),
  status: z.enum(['ACTIVE', 'INACTIVE']),
  accessType: z.enum(['ANONYMOUS', 'WHITELIST', 'DOMAIN', 'OPEN']),
  verificationMethod: z.enum(['NONE', 'EMAIL_OTP', 'SMS_OTP']),
  allowedDomains: z.string().optional(),

  // Limits
  messagesPerHour: z.string().optional(),
  messagesPerDay: z.string().optional(),
  messagesTotal: z.string().optional(),

  // Access Config
  sessionExpiryHours: z.string().optional(),
  otpExpiryMinutes: z.string().optional(),
  otpMaxAttempts: z.string().optional(),

  // Input Config
  allowAudio: z.boolean().optional(),
  allowImages: z.boolean().optional(),
  allowFiles: z.boolean().optional(),
  maxAudioDuration: z.string().optional(),
  maxImageSize: z.string().optional(),
  maxFileSize: z.string().optional(),
  allowedExtensions: z.string().optional(),
});

// Schema extendido para superadmins (requiere tenantId)
const superAdminInstanceSchema = baseInstanceSchema.extend({
  tenantId: z.string().min(1, 'Tenant ID requerido'),
});

type InstanceFormData = z.infer<typeof baseInstanceSchema> & { tenantId?: string };

function botFormToPayload(data: BotConfigurationFormData) {
  const concierge = conciergeFormToPayload(data);
  return {
    name: data.name,
    description: data.description || undefined,
    provider: data.provider,
    modelId: data.kind === 'CONCIERGE' ? data.modelId || undefined : data.modelId,
    systemPrompt: data.kind === 'CONCIERGE' ? data.systemPrompt || undefined : data.systemPrompt,
    openaiApiKey: data.openaiApiKey?.trim() || undefined,
    anthropicApiKey: data.anthropicApiKey?.trim() || undefined,
    clearOpenaiApiKey: data.clearOpenaiApiKey || undefined,
    clearAnthropicApiKey: data.clearAnthropicApiKey || undefined,
    temperature: data.temperature ? Number.parseFloat(data.temperature) : undefined,
    maxOutputTokens: data.maxOutputTokens
      ? Number.parseInt(data.maxOutputTokens, 10)
      : undefined,
    sttModelId: data.sttModelId || 'whisper-1',
    historyMessageLimit: data.historyMessageLimit
      ? Number.parseInt(data.historyMessageLimit, 10)
      : undefined,
    historyMessageMaxChars: data.historyMessageMaxChars
      ? Number.parseInt(data.historyMessageMaxChars, 10)
      : undefined,
    conversationMode: concierge.conversationMode ?? data.conversationMode,
    iconUrl: data.iconUrl || undefined,
    displayOrder: data.displayOrder ? Number.parseInt(data.displayOrder, 10) : 0,
    ...noragFormToPayload(data),
    ...concierge,
    ...infoFieldsFormToPayload(data),
  };
}

type BotHealth = {
  botId: string;
  botName: string;
  status: 'online' | 'error' | 'offline';
  lastChecked: string;
  error?: string;
};

type InstanceDataForForm = {
  tenantId?: string;
  name: string;
  description?: string | null;
  status: 'ACTIVE' | 'INACTIVE';
  accessType: ChatbotAccessType;
  verificationMethod: ChatbotVerificationMethod;
  accessConfig?: {
    allowedDomains?: string[];
    sessionExpiryHours?: number;
    otpExpiryMinutes?: number;
    otpMaxAttempts?: number;
  } | null;
  limitsConfig?: {
    messagesPerHour?: number | null;
    messagesPerDay?: number | null;
    messagesTotal?: number | null;
  } | null;
  inputConfig?: {
    audio?: { enabled?: boolean; maxDurationSeconds?: number } | null;
    images?: { enabled?: boolean; maxSizeMb?: number } | null;
    files?: { enabled?: boolean; maxSizeMb?: number; allowedExtensions?: string[] } | null;
  } | null;
};

function applyGeneralFormValues(
  instanceData: InstanceDataForForm,
  setValue: UseFormSetValue<InstanceFormData>
): void {
  setValue('name', instanceData.name);
  setValue('description', instanceData.description || '');
  setValue('status', instanceData.status);
  setValue('accessType', instanceData.accessType);
  setValue('verificationMethod', instanceData.verificationMethod);
}

function applyAccessFormValues(
  instanceData: InstanceDataForForm,
  setValue: UseFormSetValue<InstanceFormData>
): void {
  setValue('allowedDomains', instanceData.accessConfig?.allowedDomains?.join(', ') || '');
  setValue('sessionExpiryHours', instanceData.accessConfig?.sessionExpiryHours?.toString() || '24');
  setValue('otpExpiryMinutes', instanceData.accessConfig?.otpExpiryMinutes?.toString() || '15');
  setValue('otpMaxAttempts', instanceData.accessConfig?.otpMaxAttempts?.toString() || '3');
}

function applyLimitsFormValues(
  instanceData: InstanceDataForForm,
  setValue: UseFormSetValue<InstanceFormData>
): void {
  setValue('messagesPerHour', instanceData.limitsConfig?.messagesPerHour?.toString() || '');
  setValue('messagesPerDay', instanceData.limitsConfig?.messagesPerDay?.toString() || '');
  setValue('messagesTotal', instanceData.limitsConfig?.messagesTotal?.toString() || '');
}

function applyInputFormValues(
  instanceData: InstanceDataForForm,
  setValue: UseFormSetValue<InstanceFormData>
): void {
  setValue('allowAudio', instanceData.inputConfig?.audio?.enabled || false);
  setValue(
    'maxAudioDuration',
    instanceData.inputConfig?.audio?.maxDurationSeconds?.toString() || '60'
  );
  setValue('allowImages', instanceData.inputConfig?.images?.enabled || false);
  setValue('maxImageSize', instanceData.inputConfig?.images?.maxSizeMb?.toString() || '5');
  setValue('allowFiles', instanceData.inputConfig?.files?.enabled || false);
  setValue('maxFileSize', instanceData.inputConfig?.files?.maxSizeMb?.toString() || '10');
  setValue(
    'allowedExtensions',
    instanceData.inputConfig?.files?.allowedExtensions?.join(',') ||
      DEFAULT_ALLOWED_FILE_EXTENSIONS
  );
}

// Aplica los datos de una instancia al formulario para mantener el loader simple y legible.
function applyInstanceToForm(
  instanceData: InstanceDataForForm,
  isSuperAdmin: boolean,
  setValue: UseFormSetValue<InstanceFormData>
): void {
  if (isSuperAdmin) {
    setValue('tenantId', instanceData.tenantId);
  }
  applyGeneralFormValues(instanceData, setValue);
  applyAccessFormValues(instanceData, setValue);
  applyLimitsFormValues(instanceData, setValue);
  applyInputFormValues(instanceData, setValue);
}

// Construye el payload del backend a partir del formulario.
function buildInstancePayload(
  data: InstanceFormData,
  tenantId: string,
  otpEmailAccountId: string | null,
) {
  return {
    tenantId,
    name: data.name,
    description: data.description,
    status: data.status,
    accessType: data.accessType,
    verificationMethod: data.verificationMethod,
    otpEmailAccountId,
    accessConfig: {
      allowedDomains: data.allowedDomains
        ?.split(',')
        .map(d => d.trim())
        .filter(Boolean),
      sessionExpiryHours: parseInt(data.sessionExpiryHours || '24'),
      otpExpiryMinutes: parseInt(data.otpExpiryMinutes || '15'),
      otpMaxAttempts: parseInt(data.otpMaxAttempts || '3'),
    },
    limitsConfig: {
      messagesPerHour: data.messagesPerHour ? parseInt(data.messagesPerHour) : null,
      messagesPerDay: data.messagesPerDay ? parseInt(data.messagesPerDay) : null,
      messagesTotal: data.messagesTotal ? parseInt(data.messagesTotal) : null,
    },
    inputConfig: {
      audio: {
        enabled: data.allowAudio || false,
        maxDurationSeconds: parseInt(data.maxAudioDuration || '60'),
      },
      images: {
        enabled: data.allowImages || false,
        maxSizeMb: parseInt(data.maxImageSize || '5'),
      },
      files: {
        enabled: data.allowFiles || false,
        maxSizeMb: parseInt(data.maxFileSize || '10'),
        allowedExtensions: data.allowedExtensions
          ?.split(',')
          .map(e => e.trim())
          .filter(Boolean),
      },
    },
  };
}

export function ChatbotInstanceEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const isNew = id === 'new';
  const isSuperAdmin = currentUser?.role === 'SUPERADMIN';
  const hasWhatsApp = hasWhatsAppModuleAccess(currentUser);
  const hasTelegram = hasModuleFeature(currentUser, 'telegram-integration');
  const hasFacebook = hasModuleFeature(currentUser, 'facebook-integration');
  const hasInstagram = hasModuleFeature(currentUser, 'instagram-integration');
  // Schema dinámico: superadmin requiere tenantId, usuario normal no
  const dynamicSchema = useMemo(
    () => (isSuperAdmin ? superAdminInstanceSchema : baseInstanceSchema),
    [isSuperAdmin]
  );

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [bots, setBots] = useState<ChatbotBotInstance[]>([]);
  const [whitelist, setWhitelist] = useState<ChatbotWhitelistEntry[]>([]);
  const [filteredWhitelist, setFilteredWhitelist] = useState<ChatbotWhitelistEntry[]>([]);
  const [showBotForm, setShowBotForm] = useState(false);
  const [editingBotId, setEditingBotId] = useState<string | null>(null);
  const [botCatalog, setBotCatalog] = useState<ChatbotAiCatalog | null>(null);
  const [botFormInitial, setBotFormInitial] = useState<Partial<BotConfigurationFormData> | undefined>();
  const [botSaving, setBotSaving] = useState(false);
  const [infoFieldsBotId, setInfoFieldsBotId] = useState<string | null>(null);
  const [tenantKeys, setTenantKeys] = useState<TenantAiKeysResponse | null>(null);
  const [newWhitelistEmail, setNewWhitelistEmail] = useState('');
  const [whitelistSearch, setWhitelistSearch] = useState('');
  const [whatsappChannels, setWhatsappChannels] = useState<WhatsAppChannel[]>([]);
  const [telegramChannels, setTelegramChannels] = useState<TelegramChannel[]>([]);
  const [facebookChannels, setFacebookChannels] = useState<FacebookChannel[]>([]);
  const [instagramChannels, setInstagramChannels] = useState<InstagramChannel[]>([]);
  const [outboundChannels, setOutboundChannels] = useState<InstanceOutboundChannel[]>(['WHATSAPP']);
  const [loadedAccess, setLoadedAccess] = useState<{
    accessType: ChatbotAccessType;
    verificationMethod: ChatbotVerificationMethod;
  } | null>(null);
  const [otpEmailAccountId, setOtpEmailAccountId] = useState<string | null>(null);

  // Tenants para superadmin
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);

  // Health Status
  const [healthStatus, setHealthStatus] = useState<BotHealth[]>([]);
  const existingConcierge = useMemo(() => {
    const found = bots.find(bot => bot.kind === 'CONCIERGE');
    return found ? { botId: found.botId, name: found.name } : null;
  }, [bots]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<InstanceFormData>({
    resolver: zodResolver(dynamicSchema),
    defaultValues: {
      tenantId: isSuperAdmin ? '' : currentUser?.tenantId || '',
      name: '',
      description: '',
      status: 'ACTIVE',
      accessType: 'ANONYMOUS',
      verificationMethod: 'NONE',
      allowedDomains: '',
      messagesPerHour: '',
      messagesPerDay: '',
      messagesTotal: '',
      sessionExpiryHours: '24',
      otpExpiryMinutes: '15',
      otpMaxAttempts: '3',
      allowAudio: false,
      allowImages: false,
      allowFiles: false,
      maxAudioDuration: '60',
      maxImageSize: '5',
      maxFileSize: '10',
      allowedExtensions: DEFAULT_ALLOWED_FILE_EXTENSIONS,
    },
  });

  const watchedTenantId = watch('tenantId');
  const effectiveTenantId = isSuperAdmin ? watchedTenantId : currentUser?.tenantId;
  const selectedTenantRaw = tenants.find(tenant => tenant.id === effectiveTenantId)?.config.features;
  const selectedTenantFeatures: TenantFeature[] = Array.isArray(selectedTenantRaw)
    ? selectedTenantRaw.filter((item): item is TenantFeature => typeof item === 'string')
    : [];

  useEffect(() => {
    if (!effectiveTenantId) {
      setTenantKeys(null);
      return;
    }
    chatbotAdminApi.getTenantAiKeys(effectiveTenantId).then(setTenantKeys).catch(() => undefined);
  }, [effectiveTenantId]);

  useEffect(() => {
    chatbotAdminApi
      .getBotAiCatalog(effectiveTenantId ?? undefined)
      .then(setBotCatalog)
      .catch(() => undefined);
  }, [effectiveTenantId]);

  const accessType = watch('accessType');
  const verificationMethod = watch('verificationMethod');

  // Cargar tenants para superadmin
  useEffect(() => {
    if (isSuperAdmin) {
      loadTenants();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isNew && id) {
      loadInstance(id);
    }
  }, [id, isNew]);

  useEffect(() => {
    if (whitelistSearch) {
      setFilteredWhitelist(
        whitelist.filter(w => w.email.toLowerCase().includes(whitelistSearch.toLowerCase()))
      );
    } else {
      setFilteredWhitelist(whitelist);
    }
  }, [whitelistSearch, whitelist]);

  // ANONYMOUS no admite verificación; OPEN requiere OTP (nunca NONE).
  useEffect(() => {
    if (accessType === 'ANONYMOUS') {
      setValue('verificationMethod', 'NONE');
      return;
    }
    if (accessType === 'OPEN' && verificationMethod === 'NONE') {
      setValue('verificationMethod', 'EMAIL_OTP');
    }
  }, [accessType, verificationMethod, setValue]);

  const loadTenants = async () => {
    try {
      setLoadingTenants(true);
      const tenantsData = await platformAdminApi.listTenants();
      setTenants(tenantsData);
    } catch (error: any) {
      toast.error(error.message || 'Error al cargar tenants');
    } finally {
      setLoadingTenants(false);
    }
  };

  const loadInstance = async (instanceId: string) => {
    try {
      setLoading(true);
      const [instanceData, botsData, whitelistData] = await Promise.all([
        chatbotAdminApi.getInstance(instanceId),
        chatbotAdminApi.listBots(instanceId),
        chatbotAdminApi.listWhitelist(instanceId),
      ]);

      if (instanceData) {
        applyInstanceToForm(instanceData, isSuperAdmin, setValue);
        setLoadedAccess({
          accessType: instanceData.accessType,
          verificationMethod: instanceData.verificationMethod,
        });
        setOutboundChannels(
          Array.isArray(instanceData.enabledOutboundChannels)
            ? instanceData.enabledOutboundChannels
            : ['WHATSAPP'],
        );
        setOtpEmailAccountId(instanceData.otpEmailAccountId ?? null);
      } else {
        setLoadedAccess(null);
      }

      setBots(botsData);
      setWhitelist(whitelistData);
      setFilteredWhitelist(whitelistData);

      if (hasWhatsApp) {
        const allChannels = await whatsappAdminApi.listChannels();
        const botIds = new Set(
          botsData.map(bot => bot.id).filter((botId): botId is string => Boolean(botId))
        );
        setWhatsappChannels(allChannels.filter(channel => botIds.has(channel.instanceBotId)));
      } else {
        setWhatsappChannels([]);
      }

      if (hasTelegram) {
        const allTelegramChannels = await telegramAdminApi.listChannels();
        const botIds = new Set(
          botsData.map(bot => bot.id).filter((botId): botId is string => Boolean(botId))
        );
        setTelegramChannels(
          allTelegramChannels.filter(channel => botIds.has(channel.instanceBotId))
        );
      } else {
        setTelegramChannels([]);
      }

      if (hasFacebook) {
        const allFacebookChannels = await facebookAdminApi.listChannels();
        const botIds = new Set(
          botsData.map(bot => bot.id).filter((botId): botId is string => Boolean(botId))
        );
        setFacebookChannels(
          allFacebookChannels.filter(channel => botIds.has(channel.instanceBotId))
        );
      } else {
        setFacebookChannels([]);
      }

      if (hasInstagram) {
        const allInstagramChannels = await instagramAdminApi.listChannels();
        const botIds = new Set(
          botsData.map(bot => bot.id).filter((botId): botId is string => Boolean(botId))
        );
        setInstagramChannels(
          allInstagramChannels.filter(channel => botIds.has(channel.instanceBotId))
        );
      } else {
        setInstagramChannels([]);
      }
    } catch (error) {
      toast.error('Error al cargar instancia');
    } finally {
      setLoading(false);
    }
  };

  const loadBotHealth = async (instanceId: string) => {
    try {
      const health = await chatbotAdminApi.getInstanceHealth(instanceId);
      if (health && health.bots) {
        setHealthStatus(health.bots);
      }
    } catch (error) {
      console.error('Error checking health', error);
    }
  };

  // Call loadBotHealth when instance loads
  useEffect(() => {
    if (!isNew && id && !loading) {
      loadBotHealth(id);
    }
  }, [id, isNew, loading]);

  const onSubmit = async (data: InstanceFormData) => {
    try {
      setSaving(true);

      const finalTenantId = isSuperAdmin ? data.tenantId : currentUser?.tenantId;

      if (!finalTenantId) {
        toast.error('Tenant ID requerido');
        return;
      }

      if (!isNew && !loadedAccess) {
        toast.error('La instancia aún no terminó de cargar. Esperá un momento e intentá de nuevo.');
        return;
      }

      const payload = {
        ...buildInstancePayload(data, finalTenantId, otpEmailAccountId),
        ...(isSuperAdmin ? { enabledOutboundChannels: outboundChannels } : {}),
      };

      if (!isNew && loadedAccess) {
        const accessUnchanged =
          data.accessType === loadedAccess.accessType &&
          data.verificationMethod === loadedAccess.verificationMethod;
        if (accessUnchanged) {
          delete (payload as { accessType?: ChatbotAccessType }).accessType;
          delete (payload as { verificationMethod?: ChatbotVerificationMethod }).verificationMethod;
        }
      }

      if (isNew) {
        await chatbotAdminApi.createInstance(payload);
        toast.success('Instancia creada exitosamente');
        navigate('/admin/chatbot');
      } else if (id) {
        const updated = await chatbotAdminApi.updateInstance(id, payload);
        if (updated) {
          setLoadedAccess({
            accessType: updated.accessType,
            verificationMethod: updated.verificationMethod,
          });
        }
        toast.success('Instancia actualizada exitosamente');
      }
    } catch (error: unknown) {
      toast.error(getChatbotApiErrorMessage(error, 'Error al guardar'), {
        autoClose: 8000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditBot = (bot: ChatbotBotInstance) => {
    setEditingBotId(bot.botId);
    setBotFormInitial({
      name: bot.name,
      description: bot.description || '',
      provider: bot.provider,
      modelId: bot.modelId || 'gpt-4o-mini',
      systemPrompt: bot.systemPrompt || '',
      temperature: bot.temperature?.toString() || '0.7',
      maxOutputTokens: bot.maxOutputTokens?.toString() || '4096',
      sttModelId: bot.sttModelId || 'whisper-1',
      historyMessageLimit: bot.historyMessageLimit?.toString() || '40',
      historyMessageMaxChars: bot.historyMessageMaxChars?.toString() || '8000',
      conversationMode: bot.conversationMode,
      iconUrl: bot.iconUrl || '',
      displayOrder: bot.displayOrder.toString(),
      kind: bot.kind ?? 'STANDARD',
      welcomeText: bot.concierge?.welcomeText ?? '',
      webMandatory: bot.concierge?.webMandatory ?? false,
      idleMinutes: bot.concierge?.idleMinutes?.toString() ?? '30',
      pendingIdleMinutes: bot.concierge?.pendingIdleMinutes?.toString() ?? '240',
      escapeKeywords: bot.concierge?.escapeKeywords?.join(', ') ?? '',
      classifyEnabled: bot.concierge?.classifyEnabled ?? true,
      topicShiftEnabled: bot.concierge?.topicShiftEnabled ?? false,
      menuLabel: bot.menuLabel ?? '',
      menuDescription: bot.menuDescription ?? '',
      isRoutable: bot.isRoutable ?? true,
      noragEnabled: bot.norag?.enabled ?? false,
      noragEmpresaId: bot.norag?.empresaId?.toString() ?? '',
      noragBotId: bot.norag?.botId ?? '',
      noragApiUrl: bot.norag?.apiUrl ?? '',
      botInfoFieldsEnabled: bot.botInfoFieldsEnabled ?? false,
      userInfoFieldsEnabled: bot.userInfoFieldsEnabled ?? false,
      infoFields: padChatbotInfoFields(bot.infoFields),
    });
    setShowBotForm(true);
  };

  const onSubmitBot = async (data: BotConfigurationFormData) => {
    if (!id) return;

    if (!editingBotId && data.kind !== 'CONCIERGE') {
      if (!hasTenantProviderKeyForCreate(data.provider, data, tenantKeys)) {
        toast.error(providerKeyCreateErrorMessage(data.provider));
        return;
      }
    }

    setBotSaving(true);
    try {
      const botPayload = botFormToPayload(data);

      if (editingBotId) {
        await chatbotAdminApi.updateBot(id, editingBotId, botPayload);
        toast.success('Bot actualizado');
      } else {
        await chatbotAdminApi.createBot(id, botPayload);
        toast.success('Bot creado');
      }

      setShowBotForm(false);
      setEditingBotId(null);
      setBotFormInitial(undefined);
      const botsData = await chatbotAdminApi.listBots(id);
      setBots(botsData);
      if (hasWhatsApp) {
        const allChannels = await whatsappAdminApi.listChannels();
        const botIds = new Set(
          botsData.map(bot => bot.id).filter((botId): botId is string => Boolean(botId))
        );
        setWhatsappChannels(allChannels.filter(channel => botIds.has(channel.instanceBotId)));
      }
      if (hasTelegram) {
        const allTelegramChannels = await telegramAdminApi.listChannels();
        const botIds = new Set(
          botsData.map(bot => bot.id).filter((botId): botId is string => Boolean(botId))
        );
        setTelegramChannels(
          allTelegramChannels.filter(channel => botIds.has(channel.instanceBotId))
        );
      }
      if (hasFacebook) {
        const allFacebookChannels = await facebookAdminApi.listChannels();
        const botIds = new Set(
          botsData.map(bot => bot.id).filter((botId): botId is string => Boolean(botId))
        );
        setFacebookChannels(
          allFacebookChannels.filter(channel => botIds.has(channel.instanceBotId))
        );
      }
      if (hasInstagram) {
        const allInstagramChannels = await instagramAdminApi.listChannels();
        const botIds = new Set(
          botsData.map(bot => bot.id).filter((botId): botId is string => Boolean(botId))
        );
        setInstagramChannels(
          allInstagramChannels.filter(channel => botIds.has(channel.instanceBotId))
        );
      }
      if (id) loadBotHealth(id);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Error al guardar bot';
      toast.error(message);
    } finally {
      setBotSaving(false);
    }
  };

  const handleDeleteBot = async (botId: string) => {
    if (!id || !confirm('¿Eliminar este bot?')) return;

    try {
      await chatbotAdminApi.deleteBot(id, botId);
      toast.success('Bot eliminado');
      setBots(bots.filter(b => b.botId !== botId));
    } catch (error) {
      toast.error('Error al eliminar bot');
    }
  };

  const handleAddWhitelist = async () => {
    if (!id || !newWhitelistEmail.trim()) return;

    try {
      await chatbotAdminApi.addToWhitelist(id, newWhitelistEmail.trim());
      toast.success('Email agregado');
      setNewWhitelistEmail('');
      const whitelistData = await chatbotAdminApi.listWhitelist(id);
      setWhitelist(whitelistData);
    } catch (error) {
      toast.error('Error al agregar email');
    }
  };

  const handleRemoveWhitelist = async (email: string) => {
    if (!id) return;

    try {
      await chatbotAdminApi.removeFromWhitelist(id, email);
      toast.success('Email eliminado');
      setWhitelist(whitelist.filter(w => w.email !== email));
    } catch (error) {
      toast.error('Error al eliminar email');
    }
  };

  const handleImportWhitelist = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!id || !e.target.files?.[0]) return;

    try {
      const result = await chatbotAdminApi.whitelistImport(id, e.target.files[0]);
      toast.success(`Importado: ${result.imported}, Fallidos: ${result.failed}`);
      const whitelistData = await chatbotAdminApi.listWhitelist(id);
      setWhitelist(whitelistData);
    } catch (error) {
      toast.error('Error al importar whitelist');
    }
  };

  const handleExportWhitelist = async () => {
    if (!id) return;
    try {
      const blob = await chatbotAdminApi.whitelistExport(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'whitelist.csv';
      a.click();
    } catch (error) {
      toast.error('Error al exportar whitelist');
    }
  };

  const showVerificationOptions = accessType !== 'ANONYMOUS';
  const showDomainInput = accessType === 'DOMAIN';
  const showWhitelist = accessType === 'WHITELIST' && !isNew;

  if (loading) {
    return (
      <div className='flex justify-center py-12'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
      </div>
    );
  }

  return (
    <div className='container mx-auto py-6 max-w-4xl'>
      <Button variant='ghost' onClick={() => navigate('/admin/chatbot')} className='mb-4'>
        <ArrowLeft className='h-4 w-4 mr-2' />
        Volver
      </Button>

      <Tabs defaultValue='general' className='w-full'>
        <TabsList className='grid w-full grid-cols-3'>
          <TabsTrigger value='general'>General</TabsTrigger>
          <TabsTrigger value='bots' disabled={isNew}>
            Bots
          </TabsTrigger>
          <TabsTrigger value='whitelist' disabled={!showWhitelist}>
            Whitelist
          </TabsTrigger>
        </TabsList>

        <form onSubmit={handleSubmit(onSubmit)}>
          <TabsContent value='general'>
            <Card>
              <CardHeader>
                <CardTitle>{isNew ? 'Nueva Instancia' : 'Editar Instancia'}</CardTitle>
                <CardDescription>Configuración completa de la instancia</CardDescription>
              </CardHeader>
              <CardContent className='space-y-6'>
                {isSuperAdmin && (
                  <div className='space-y-3'>
                    <Label>Tenant *</Label>

                    {loadingTenants ? (
                      <div className='flex items-center gap-2 text-sm text-muted-foreground py-2'>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary'></div>
                        Cargando tenants...
                      </div>
                    ) : (
                      <>
                        <select
                          id='tenantId'
                          {...register('tenantId')}
                          className='w-full border border-input rounded-md p-2 bg-background'
                        >
                          <option value=''>Selecciona un tenant existente</option>
                          {tenants.map(tenant => (
                            <option key={tenant.id} value={tenant.id}>
                              {tenant.name}
                            </option>
                          ))}
                        </select>

                        {tenants.length === 0 && !loadingTenants && (
                          <p className='text-xs text-muted-foreground'>
                            No hay tenants existentes disponibles.
                          </p>
                        )}
                      </>
                    )}

                    {errors.tenantId && (
                      <p className='text-sm text-red-500'>{errors.tenantId.message}</p>
                    )}
                  </div>
                )}

                <div className='grid gap-4 md:grid-cols-2'>
                  <div>
                    <Label htmlFor='name'>Nombre *</Label>
                    <Input id='name' {...register('name')} />
                    {errors.name && <p className='text-sm text-red-500'>{errors.name.message}</p>}
                  </div>
                  <div className='flex items-center gap-2 pt-8'>
                    <Switch
                      id='status'
                      checked={watch('status') === 'ACTIVE'}
                      onCheckedChange={checked =>
                        setValue('status', checked ? 'ACTIVE' : 'INACTIVE')
                      }
                    />
                    <Label htmlFor='status'>Activo</Label>
                  </div>
                </div>

                <div>
                  <Label htmlFor='description'>Descripción</Label>
                  <Textarea id='description' {...register('description')} />
                </div>

                {/* Configuración Avanzada - Access Control */}
                <div className='space-y-4 border-t pt-6'>
                  <h3 className='text-lg font-medium'>Control de Acceso</h3>
                  <div className='grid gap-4 md:grid-cols-2'>
                    <div>
                      <Label htmlFor='accessType'>Tipo de Acceso</Label>
                      <select
                        id='accessType'
                        {...register('accessType')}
                        className='w-full border border-input bg-background text-foreground rounded-md p-2'
                      >
                        <option value='ANONYMOUS'>Anónimo</option>
                        <option value='WHITELIST'>Whitelist</option>
                        <option value='DOMAIN'>Dominio</option>
                        <option value='OPEN'>Abierto con verificación</option>
                      </select>
                    </div>

                    {showVerificationOptions && (
                      <div>
                        <Label htmlFor='verificationMethod'>Método de Verificación</Label>
                        <select
                          id='verificationMethod'
                          {...register('verificationMethod')}
                          className='w-full border border-input bg-background text-foreground rounded-md p-2'
                        >
                          {accessType !== 'OPEN' && (
                            <option value='NONE'>Sin verificación</option>
                          )}
                          <option value='EMAIL_OTP'>Código por correo</option>
                          <option value='SMS_OTP'>SMS OTP (Fase 2)</option>
                        </select>
                        {verificationMethod === 'EMAIL_OTP' && !isNew && id ? (
                          <div className='mt-2'>
                            <InstanceOtpEmailSection
                              tenantId={effectiveTenantId ?? undefined}
                              selectedAccountId={otpEmailAccountId}
                              onSelectAccount={async accountId => {
                                await chatbotAdminApi.updateInstance(id, { otpEmailAccountId: accountId });
                                setOtpEmailAccountId(accountId);
                              }}
                            />
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>

                  {showDomainInput && (
                    <div>
                      <Label htmlFor='allowedDomains'>
                        Dominios permitidos (separados por coma)
                      </Label>
                      <Input
                        id='allowedDomains'
                        {...register('allowedDomains')}
                        placeholder='ejemplo.com, empresa.com'
                      />
                    </div>
                  )}

                  <div className='grid gap-4 md:grid-cols-3'>
                    <div>
                      <Label htmlFor='sessionExpiryHours'>Expiración Sesión (horas)</Label>
                      <Input type='number' {...register('sessionExpiryHours')} />
                    </div>
                    <div>
                      <Label htmlFor='otpExpiryMinutes'>Expiración OTP (minutos)</Label>
                      <Input type='number' {...register('otpExpiryMinutes')} />
                    </div>
                    <div>
                      <Label htmlFor='otpMaxAttempts'>Intentos Máx. OTP</Label>
                      <Input type='number' {...register('otpMaxAttempts')} />
                    </div>
                  </div>
                </div>

                {/* Input Config */}
                <div className='space-y-4 border-t pt-4'>
                  <h3 className='text-lg font-medium'>Configuración de Entrada</h3>
                  <div className='grid gap-4 md:grid-cols-3'>
                    <div className='space-y-2 border p-3 rounded-md'>
                      <div className='flex items-center gap-2'>
                        <Switch
                          checked={watch('allowAudio')}
                          onCheckedChange={c => setValue('allowAudio', c)}
                        />
                        <Label>Audio</Label>
                      </div>
                      {watch('allowAudio') && (
                        <div>
                          <Label className='text-xs'>Máx. Duración (seg)</Label>
                          <Input type='number' {...register('maxAudioDuration')} className='h-8' />
                        </div>
                      )}
                    </div>

                    <div className='space-y-2 border p-3 rounded-md'>
                      <div className='flex items-center gap-2'>
                        <Switch
                          checked={watch('allowImages')}
                          onCheckedChange={c => setValue('allowImages', c)}
                        />
                        <Label>Imágenes</Label>
                      </div>
                      {watch('allowImages') && (
                        <div>
                          <Label className='text-xs'>Máx. Tamaño (MB)</Label>
                          <Input type='number' {...register('maxImageSize')} className='h-8' />
                        </div>
                      )}
                    </div>

                    <div className='space-y-2 border p-3 rounded-md'>
                      <div className='flex items-center gap-2'>
                        <Switch
                          checked={watch('allowFiles')}
                          onCheckedChange={c => setValue('allowFiles', c)}
                        />
                        <Label>Archivos</Label>
                      </div>
                      {watch('allowFiles') && (
                        <div className='space-y-2'>
                          <div>
                            <Label className='text-xs'>Máx. Tamaño (MB)</Label>
                            <Input type='number' {...register('maxFileSize')} className='h-8' />
                          </div>
                          <div>
                            <Label className='text-xs'>Extensiones</Label>
                            <Input
                              {...register('allowedExtensions')}
                              className='h-8'
                              placeholder='.pdf,.doc'
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Limits */}
                <div className='space-y-4 border-t pt-4'>
                  <h3 className='text-lg font-medium'>Límites de Uso</h3>
                  <div className='grid gap-4 md:grid-cols-3'>
                    <div>
                      <Label htmlFor='messagesPerHour'>Mensajes por hora</Label>
                      <Input
                        type='number'
                        {...register('messagesPerHour')}
                        placeholder='Sin límite'
                      />
                    </div>
                    <div>
                      <Label htmlFor='messagesPerDay'>Mensajes por día</Label>
                      <Input
                        type='number'
                        {...register('messagesPerDay')}
                        placeholder='Sin límite'
                      />
                    </div>
                    <div>
                      <Label htmlFor='messagesTotal'>Mensajes totales</Label>
                      <Input
                        type='number'
                        {...register('messagesTotal')}
                        placeholder='Sin límite'
                      />
                    </div>
                  </div>
                </div>

                {isSuperAdmin ? (
                  <InstanceOutboundChannelsCard
                    tenantFeatures={selectedTenantFeatures}
                    value={outboundChannels}
                    onChange={setOutboundChannels}
                  />
                ) : null}

                {!isNew && id ? (
                  <InstanceCampaignChannelsCard
                    instanceId={id}
                    tenantId={effectiveTenantId ?? undefined}
                    bots={bots}
                    whatsappChannels={whatsappChannels}
                    enabledOutboundChannels={outboundChannels}
                    tenantFeatures={isSuperAdmin ? selectedTenantFeatures : []}
                  />
                ) : null}

                {/* Botón Guardar al final de todo */}
                <div className='flex justify-end pt-4 border-t'>
                  <Button type='submit' disabled={saving}>
                    <Save className='h-4 w-4 mr-2' />
                    {saving ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </form>

        <TabsContent value='bots' className='space-y-4'>
          {effectiveTenantId && (
            <TenantAiKeysCard tenantId={effectiveTenantId} title='API keys del tenant (fallback para bots)' />
          )}
          <Card>
            <CardHeader className='flex flex-row items-center justify-between'>
              <div>
                <CardTitle>Bots IA</CardTitle>
                <CardDescription>Configure los bots asociados a esta instancia</CardDescription>
              </div>
              <Button
                size='sm'
                onClick={() => {
                  setEditingBotId(null);
                  setBotFormInitial(undefined);
                  setShowBotForm(!showBotForm);
                }}
              >
                <Plus className='h-4 w-4 mr-2' />
                Agregar Bot
              </Button>
            </CardHeader>
            <CardContent>
              {showBotForm && (
                <div className='border rounded-lg p-4 mb-4 bg-muted/50'>
                  <BotConfigurationForm
                    mode={editingBotId ? 'edit' : 'create'}
                    initialValues={botFormInitial}
                    catalog={botCatalog}
                    aiReady={editingBotId ? bots.find(b => b.botId === editingBotId)?.aiReady : false}
                    openaiApiKeyStatus={
                      editingBotId
                        ? bots.find(b => b.botId === editingBotId)?.openaiApiKey
                        : undefined
                    }
                    anthropicApiKeyStatus={
                      editingBotId
                        ? bots.find(b => b.botId === editingBotId)?.anthropicApiKey
                        : undefined
                    }
                    noragStatus={
                      editingBotId ? bots.find(b => b.botId === editingBotId)?.norag : undefined
                    }
                    showDisplayOrder
                    showInstanceCatalogFields
                    existingConcierge={existingConcierge}
                    editingBotId={editingBotId}
                    classifyNeedsModel={
                      editingBotId
                        ? !bots.find(b => b.botId === editingBotId)?.aiReady
                        : !tenantKeys?.openaiApiKey.configured &&
                          !tenantKeys?.anthropicApiKey.configured
                    }
                    saving={botSaving}
                    onSubmit={onSubmitBot}
                    onCancel={() => {
                      setShowBotForm(false);
                      setEditingBotId(null);
                      setBotFormInitial(undefined);
                    }}
                  />
                </div>
              )}

              {bots.length === 0 ? (
                <p className='text-center text-muted-foreground py-4'>No hay bots configurados</p>
              ) : (
                <div className='space-y-2'>
                  {bots.map(bot => {
                    const health = healthStatus.find(h => h.botId === bot.botId);
                    const healthErrorSuffix = health?.error ? `\nError: ${health.error}` : '';
                    const healthTooltip = health
                      ? `Estado: ${health.status}${healthErrorSuffix}`
                      : '';
                    return (
                      <div
                        key={bot.botId}
                        className='flex items-center justify-between border rounded-lg p-3'
                      >
                        <div className='flex items-center gap-3'>
                          {bot.iconUrl && (
                            <img src={bot.iconUrl} alt='icon' className='w-8 h-8 rounded-full' />
                          )}
                          <div>
                            <div className='flex items-center gap-2'>
                              <div className='font-medium'>{bot.name}</div>
                              {bot.kind === 'CONCIERGE' && (
                                <span className='rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700'>
                                  Conserje
                                </span>
                              )}
                              {health && (
                                <div title={healthTooltip}>
                                  {health.status === 'online' ? (
                                    <CheckCircle className='h-4 w-4 text-green-500' />
                                  ) : (
                                    <div className='flex items-center gap-1 text-red-500'>
                                      <AlertCircle className='h-4 w-4' />
                                      {health.error && (
                                        <span className='text-xs font-semibold'>
                                          {health.error}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className='text-sm text-muted-foreground'>
                              {bot.modelId || 'Sin modelo'} ·{' '}
                              {bot.aiReady ? 'IA lista' : 'IA pendiente de configuración'}
                            </div>
                          </div>
                        </div>
                        <div className='flex gap-2'>
                          <Button variant='outline' size='sm' onClick={() => handleEditBot(bot)}>
                            Editar aquí
                          </Button>
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={() => setInfoFieldsBotId(bot.botId)}
                          >
                            Info / aviso
                          </Button>
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={() => navigate(`/admin/chatbot/bots/${bot.botId}`)}
                          >
                            Configuración completa
                          </Button>
                          <Button
                            variant='destructive'
                            size='sm'
                            onClick={() => handleDeleteBot(bot.botId)}
                          >
                            <Trash2 className='h-4 w-4' />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='whitelist' className='space-y-6'>
          <Card>
            <CardHeader>
              <CardTitle>Whitelist de emails (chat web)</CardTitle>
              <CardDescription>
                Emails autorizados para acceder al chatbot por navegador. No aplica a WhatsApp.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className='flex flex-col gap-4 mb-4'>
                <div className='flex gap-2'>
                  <Input
                    value={newWhitelistEmail}
                    onChange={e => setNewWhitelistEmail(e.target.value)}
                    placeholder='email@ejemplo.com'
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddWhitelist())}
                  />
                  <Button onClick={handleAddWhitelist}>Agregar</Button>
                </div>

                <div className='flex justify-between items-center gap-4'>
                  <div className='relative flex-1'>
                    <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
                    <Input
                      placeholder='Buscar email...'
                      value={whitelistSearch}
                      onChange={e => setWhitelistSearch(e.target.value)}
                      className='pl-8'
                    />
                  </div>
                  <div className='flex gap-2'>
                    <div className='relative'>
                      <input
                        type='file'
                        id='import-whitelist'
                        className='hidden'
                        accept='.csv,.txt'
                        onChange={handleImportWhitelist}
                      />
                      <Button
                        variant='outline'
                        onClick={() => document.getElementById('import-whitelist')?.click()}
                      >
                        <Upload className='h-4 w-4 mr-2' /> Importar
                      </Button>
                    </div>
                    <Button variant='outline' onClick={handleExportWhitelist}>
                      <Download className='h-4 w-4 mr-2' /> Exportar
                    </Button>
                  </div>
                </div>
              </div>

              {filteredWhitelist.length === 0 ? (
                <p className='text-center text-muted-foreground py-4'>
                  No hay emails en la whitelist
                </p>
              ) : (
                <div className='space-y-2 max-h-96 overflow-y-auto'>
                  {filteredWhitelist.map(entry => (
                    <div
                      key={entry.id}
                      className='flex items-center justify-between border rounded-lg p-2'
                    >
                      <span>{entry.email}</span>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => handleRemoveWhitelist(entry.email)}
                      >
                        <Trash2 className='h-4 w-4 text-red-500' />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {hasWhatsApp && (
            <div className='space-y-4'>
              <div>
                <h2 className='text-lg font-semibold'>WhatsApp — teléfonos autorizados</h2>
                <p className='text-sm text-muted-foreground mt-1'>
                  Números que pueden escribir por WhatsApp a cada bot con canal configurado. La
                  configuración completa del canal está en{' '}
                  <Link to='/admin/whatsapp' className='underline font-medium'>
                    Administración → WhatsApp
                  </Link>
                  .
                </p>
              </div>
              {whatsappChannels.length === 0 ? (
                <Card>
                  <CardContent className='pt-6'>
                    <p className='text-sm text-muted-foreground'>
                      Ningún bot de esta instancia tiene canal WhatsApp. Creá uno en Administración
                      → WhatsApp y vinculalo al bot correspondiente.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                whatsappChannels.map(channel => {
                  const bot = bots.find(item => item.id === channel.instanceBotId);
                  const botLabel = bot?.name ?? channel.instanceBotId;
                  return (
                    <WhatsAppPhoneWhitelistSection
                      key={channel.id}
                      channelId={channel.id}
                      accessMode={channel.accessMode}
                      botLabel={botLabel}
                    />
                  );
                })
              )}
            </div>
          )}

          {hasTelegram && (
            <div className='space-y-4'>
              <div>
                <h2 className='text-lg font-semibold'>Telegram — usuarios autorizados</h2>
                <p className='text-sm text-muted-foreground mt-1'>
                  IDs de usuario que pueden escribir por Telegram. Configuración del canal en{' '}
                  <Link to='/admin/telegram' className='underline font-medium'>
                    Administración → Telegram
                  </Link>
                  .
                </p>
              </div>
              {telegramChannels.length === 0 ? (
                <Card>
                  <CardContent className='pt-6'>
                    <p className='text-sm text-muted-foreground'>
                      Ningún bot de esta instancia tiene canal Telegram. Creá uno en Administración →
                      Telegram.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                telegramChannels.map(channel => {
                  const bot = bots.find(item => item.id === channel.instanceBotId);
                  const botLabel = bot?.name ?? channel.instanceBotId;
                  return (
                    <TelegramUserWhitelistSection
                      key={channel.id}
                      channelId={channel.id}
                      accessMode={channel.accessMode}
                      botLabel={botLabel}
                    />
                  );
                })
              )}
            </div>
          )}

          {hasFacebook && (
            <div className='space-y-4'>
              <div>
                <h2 className='text-lg font-semibold'>Facebook Messenger — usuarios autorizados</h2>
                <p className='text-sm text-muted-foreground mt-1'>
                  PSIDs que pueden escribir por Messenger. Configuración del canal en{' '}
                  <Link to='/admin/facebook' className='underline font-medium'>
                    Administración → Facebook
                  </Link>
                  .
                </p>
              </div>
              {facebookChannels.length === 0 ? (
                <Card>
                  <CardContent className='pt-6'>
                    <p className='text-sm text-muted-foreground'>
                      Ningún bot de esta instancia tiene canal Messenger. Creá uno en Administración →
                      Facebook.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                facebookChannels.map(channel => {
                  const bot = bots.find(item => item.id === channel.instanceBotId);
                  const botLabel = bot?.name ?? channel.instanceBotId;
                  return (
                    <FacebookUserWhitelistSection
                      key={channel.id}
                      channelId={channel.id}
                      accessMode={channel.accessMode}
                      botLabel={botLabel}
                    />
                  );
                })
              )}
            </div>
          )}

          {hasInstagram && (
            <div className='space-y-4'>
              <div>
                <h2 className='text-lg font-semibold'>Instagram DM — usuarios autorizados</h2>
                <p className='text-sm text-muted-foreground mt-1'>
                  IGSIDs que pueden escribir por Instagram. Configuración del canal en{' '}
                  <Link to='/admin/instagram' className='underline font-medium'>
                    Administración → Instagram
                  </Link>
                  .
                </p>
              </div>
              {instagramChannels.length === 0 ? (
                <Card>
                  <CardContent className='pt-6'>
                    <p className='text-sm text-muted-foreground'>
                      Ningún bot de esta instancia tiene canal Instagram. Creá uno en Administración →
                      Instagram.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                instagramChannels.map(channel => {
                  const bot = bots.find(item => item.id === channel.instanceBotId);
                  const botLabel = bot?.name ?? channel.instanceBotId;
                  return (
                    <InstagramUserWhitelistSection
                      key={channel.id}
                      channelId={channel.id}
                      accessMode={channel.accessMode}
                      botLabel={botLabel}
                    />
                  );
                })
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <BotInfoFieldsQuickDialog
        botId={infoFieldsBotId}
        open={Boolean(infoFieldsBotId)}
        onOpenChange={open => {
          if (!open) setInfoFieldsBotId(null);
        }}
      />

    </div>
  );
}

export default ChatbotInstanceEditPage;
